import { column, Schema, Table } from '@powersync/web'

const users = new Table({
  email: column.text,
  name: column.text,
  role: column.text,
  company: column.text,
  stripeCustomerId: column.text,
  supabaseAuthId: column.text,
  createdAt: column.text,
  updatedAt: column.text,
  fcmToken: column.text,
  deletedAt: column.text,
  // Demo-account state (synced on the user's own row + admin projection).
  phone: column.text,
  account_type: column.text,
  account_status: column.text,
  demo_expires_at: column.text,
  demo_analysis_limit: column.integer,
  approved_at: column.text,
  approved_by: column.text,
}, { indexes: {} })

const projects = new Table({
  name: column.text,
  description: column.text,
  address: column.text,
  status: column.text,
  startDate: column.text,
  endDate: column.text,
  creatorId: column.text,
  createdAt: column.text,
  updatedAt: column.text,
  isActive: column.integer,
  userId: column.text,
  latitude: column.real,
  longitude: column.real,
}, { indexes: { by_creator: ['creatorId'] } })

const project_members = new Table({
  projectId: column.text,
  userId: column.text,
  invitedBy: column.text,
  assignedAt: column.text,
}, { indexes: { by_project: ['projectId'], by_user: ['userId'] } })

const contractors = new Table({
  companyName: column.text,
  contactPerson: column.text,
  email: column.text,
  phone: column.text,
  trade: column.text,
  createdAt: column.text,
  updatedAt: column.text,
  createdBy: column.text,
}, { indexes: { by_trade: ['trade'] } })

const project_contractors = new Table({
  projectId: column.text,
  contractorId: column.text,
  assignedAt: column.text,
}, { indexes: { by_project: ['projectId'], by_contractor: ['contractorId'] } })

const defect_tickets = new Table({
  defectIds: column.text,
  contractorId: column.text,
  projectId: column.text,
  inspectionId: column.text,
  deadline: column.text,
  status: column.text,
  sentAt: column.text,
  resolvedAt: column.text,
  notes: column.text,
  emailThreadId: column.text,
  createdById: column.text,
  createdAt: column.text,
  updatedAt: column.text,
  subject: column.text,
  publishToKnowledgeBase: column.integer,
  type: column.text,
  request_description: column.text,
  send_history: column.text,
}, { indexes: { by_project: ['projectId'], by_status: ['status'], by_inspection: ['inspectionId'] } })

const analyses = new Table({
  imageUrl: column.text,
  imageWidth: column.integer,
  imageHeight: column.integer,
  imageHash: column.text,
  userId: column.text,
  projectId: column.text,
  locationDetails: column.text,
  status: column.text,
  summary: column.text,
  confidence: column.real,
  mode: column.text,
  analysisTime: column.integer,
  isDuplicate: column.integer,
  originalAnalysisId: column.text,
  offlineQueue: column.integer,
  createdAt: column.text,
  updatedAt: column.text,
  geminiFileUri: column.text,
  geminiFileExpiry: column.text,
  analysisDetails: column.text,
  prompt_tokens: column.integer,
  completion_tokens: column.integer,
  total_tokens: column.integer,
  estimated_cost: column.real,
  floorPlanId: column.text,
  pinX: column.real,
  pinY: column.real,
  roomId: column.text,
  pending_inspection_id: column.text,
  pending_room_id: column.text,
  pending_room_label: column.text,
  analyze_lock_token: column.text,
  analyze_started_at: column.text,
  last_error: column.text,
  retry_count: column.integer,
}, { indexes: {
  by_user: ['userId'],
  by_project: ['projectId'],
  by_created: ['createdAt'],
  // Hot list query: WHERE userId = ? [AND projectId = ?] ORDER BY createdAt DESC LIMIT 12.
  // SQLite can traverse an ASC compound index in reverse, so the (userId, createdAt)
  // pair eliminates the sort step. Single-column indexes above are kept because
  // other queries (e.g. count-by-project) still benefit from them on their own.
  by_user_created: ['userId', 'createdAt'],
  by_project_created: ['projectId', 'createdAt'],
} })

const analysis_metadata = new Table({
  analysis_id: column.text,
  success: column.integer,
  error_message: column.text,
  gemini_model: column.text,
  temperature: column.real,
  max_output_tokens: column.integer,
  final_prompt: column.text,
  raw_gemini_response: column.text,
  finish_reason: column.text,
  enabled_defect_types: column.text,
  enabled_trades: column.text,
  analysis_language: column.text,
  confidence_threshold: column.real,
  max_defects_to_show: column.integer,
  user_enabled_defects: column.text,
  location_details: column.text,
  analysis_details: column.text,
  prompt_tokens: column.integer,
  completion_tokens: column.integer,
  total_tokens: column.integer,
  created_at: column.text,
  filter_by_trades: column.text,
}, { indexes: { by_analysis: ['analysis_id'] } })

const defect_reports = new Table({
  analysisId: column.text,
  projectId: column.text,
  userId: column.text,
  type: column.text,
  severity: column.text,
  status: column.text,
  description: column.text,
  recommendation: column.text,
  confidence: column.real,
  boundingBoxX: column.real,
  boundingBoxY: column.real,
  boundingBoxW: column.real,
  boundingBoxH: column.real,
  confirmedAt: column.text,
  resolvedAt: column.text,
  createdAt: column.text,
  updatedAt: column.text,
  isManual: column.integer,
  defect_type_id: column.text,
}, { indexes: { by_analysis: ['analysisId'], by_project: ['projectId'], by_user: ['userId'] } })

const trades = new Table({
  analysisId: column.text,
  type: column.text,
  confidence: column.real,
  createdAt: column.text,
  updatedAt: column.text,
}, { indexes: { by_analysis: ['analysisId'] } })

const inspections = new Table({
  project_id: column.text,
  title: column.text,
  inspection_date: column.text,
  status: column.text,
  participants: column.text,
  notes: column.text,
  location_filter: column.text,
  status_filter: column.text,
  pdf_storage_path: column.text,
  pdf_generated_at: column.text,
  created_by: column.text,
  finalized_by: column.text,
  finalized_at: column.text,
  created_at: column.text,
  updated_at: column.text,
}, { indexes: { by_project: ['project_id'], by_created_by: ['created_by'] } })

const inspection_defects = new Table({
  inspection_id: column.text,
  defect_ticket_id: column.text,
  defect_report_id: column.text,
  custom_location: column.text,
  custom_description: column.text,
  sort_order: column.integer,
  created_at: column.text,
  section_tag: column.text,
  assigned_trade: column.text,
  analysis_id: column.text,
}, { indexes: { by_inspection: ['inspection_id'] } })

const defect_types = new Table({
  slug: column.text,
  display_name_de: column.text,
  display_name_en: column.text,
  display_name_ar: column.text,
  prompt_description: column.text,
  trade: column.text,
  hex_color: column.text,
  default_severity: column.text,
  is_safety_defect: column.integer,
  is_active: column.integer,
  is_system: column.integer,
  sort_order: column.integer,
  created_at: column.text,
  updated_at: column.text,
  accuracy_score: column.real,
  status: column.text,
  agent_session_data: column.text,
  agent_phase: column.integer,
}, { indexes: { by_slug: ['slug'] } })

const defect_solutions = new Table({
  defectType: column.text,
  title: column.text,
  description: column.text,
  solutions: column.text,
  normen: column.text,
  estimatedCost: column.text,
  severity: column.text,
  preventionTips: column.text,
  customImage: column.text,
  isCustom: column.integer,
  createdAt: column.text,
  updatedAt: column.text,
}, { indexes: { by_defect_type: ['defectType'] } })

const user_detection_preferences = new Table({
  user_id: column.text,
  confidence_threshold: column.real,
  max_defects_to_show: column.integer,
  enabled_trades: column.text,
  custom_trades: column.text,
  enabled_defects: column.text,
  custom_defects: column.text,
  created_at: column.text,
  updated_at: column.text,
  ui_language: column.text,
}, { indexes: { by_user: ['user_id'] } })

const email_templates = new Table({
  name: column.text,
  subject: column.text,
  bodyHtml: column.text,
  isDefault: column.integer,
  createdById: column.text,
  createdAt: column.text,
  updatedAt: column.text,
}, { indexes: { by_creator: ['createdById'] } })

const project_rooms = new Table({
  floor_plan_id: column.text,
  name: column.text,
  type: column.text,
  bounding_box: column.text,
  color: column.text,
  ai_generated: column.integer,
  sort_order: column.integer,
  created_at: column.text,
  unit: column.text,
}, { indexes: { by_floor_plan: ['floor_plan_id'] } })

const project_floor_plans = new Table({
  project_id: column.text,
  building: column.text,
  floor: column.text,
  label: column.text,
  storage_path: column.text,
  sort_order: column.integer,
  created_by: column.text,
  created_at: column.text,
  updated_at: column.text,
}, { indexes: { by_project: ['project_id'] } })

const subscriptions = new Table({
  userId: column.text,
  stripeSubscriptionId: column.text,
  stripePriceId: column.text,
  status: column.text,
  currentPeriodStart: column.text,
  currentPeriodEnd: column.text,
  cancelAtPeriodEnd: column.integer,
  trialEnd: column.text,
  createdAt: column.text,
  updatedAt: column.text,
}, { indexes: { by_user: ['userId'] } })

const system_settings = new Table({
  modelType: column.text,
  confidenceThreshold: column.real,
  maxDefectsToShow: column.integer,
  updatedAt: column.text,
  gemini_api_key: column.text,
  gemini_model: column.text,
  gemini_vision_prompt: column.text,
  enabled_trades: column.text,        // JSONB on server
  custom_trades: column.text,         // JSONB on server
  enabled_defects: column.text,       // JSONB on server
  custom_defects: column.text,        // JSONB on server
  offline_auto_sync: column.integer,
  offline_max_queue_size: column.integer,
  push_notifications_enabled: column.integer,
  analysis_language: column.text,
  feature_flags: column.text,         // JSONB on server
  min_app_version: column.text,       // force-update gate floor (semver), null = off
}, { indexes: {} })

const reminder_settings = new Table({
  projectId: column.text,
  firstReminderEnabled: column.integer,
  firstReminderDaysBefore: column.integer,
  secondReminderEnabled: column.integer,
  secondReminderDaysBefore: column.integer,
  mahnungEnabled: column.integer,
  mahnungDaysAfter: column.integer,
  firstReminderTemplateId: column.text,
  secondReminderTemplateId: column.text,
  mahnungTemplateId: column.text,
  createdById: column.text,
  createdAt: column.text,
  updatedAt: column.text,
}, { indexes: { by_project: ['projectId'] } })

const reminder_log = new Table({
  ticketId: column.text,
  reminderType: column.text,
  sentTo: column.text,
  sentAt: column.text,
  emailStatus: column.text,
  errorMessage: column.text,
  ticketDeadline: column.text,
  daysFromDeadline: column.integer,
  createdAt: column.text,
}, { indexes: { by_ticket: ['ticketId'], by_sent: ['sentAt'] } })

// Client-only: fast offline thumbnail preview until server thumb lands.
// `localOnly` keeps this out of the PowerSync sync stream — no server
// counterpart, no risk of narrow-projection updates nulling it.
const analysis_local_thumbs = new Table({
  analysis_id: column.text,
  data_url: column.text,
  created_at: column.text,
}, { localOnly: true, indexes: { by_analysis: ['analysis_id'] } })

// Client-only: poisoned CRUD ops parked by the upload connector (fatal PG
// error or exhausted retry budget — see dead-letters.ts). Full payload kept
// for recovery; a report-dead-letter intent ships a copy to the server.
// localOnly: writing here can never create new CRUD ops.
const crud_dead_letters = new Table({
  op_table: column.text,
  // ps_crud clientIds are monotonic per device and never reused, so this is a
  // forever-unique replay key per device — used to make parking idempotent
  // when tx.complete() fails and PowerSync re-uploads the same poisoned tx.
  client_op_id: column.integer,
  op_type: column.text,        // PUT | PATCH | DELETE
  row_id: column.text,
  payload: column.text,        // JSON of the op's opData
  error_code: column.text,
  error_message: column.text,
  discarded_at: column.text,
}, { localOnly: true, indexes: { by_discarded: ['discarded_at'], by_client_op: ['client_op_id'] } })

const usage_records = new Table({
  userId: column.text,
  month: column.text,
  analysisCount: column.integer,
  lastUpdated: column.text,
  createdAt: column.text,
  prompttokens: column.integer,
  completiontokens: column.integer,
  totaltokens: column.integer,
  totalcost: column.real,
  input_price_per_million: column.real,
  output_price_per_million: column.real,
  pricing_currency: column.text,
}, { indexes: { by_user_month: ['userId', 'month'] } })

const defect_ticket_submissions = new Table({
  defect_ticket_id: column.text,
  defect_report_id: column.text,
  status: column.text,
  photos: column.text,
  note: column.text,
  submitted_at: column.text,
  reviewed_at: column.text,
  reviewed_by: column.text,
  review_note: column.text,
  created_at: column.text,
  updated_at: column.text,
}, { indexes: { by_ticket: ['defect_ticket_id'], by_report: ['defect_report_id'] } })

// Aufgaben-Boards. `position` is a fractional string key, not an integer —
// see src/lib/fractional-index.ts. `archived` is a PG boolean; the house
// convention maps booleans to column.integer and writes them as 1/0, the same
// way defect_reports."isManual" does.
const board_lists = new Table({
  project_id: column.text,
  name: column.text,
  position: column.text,
  color: column.text,
  archived: column.integer,
  created_by: column.text,
  created_at: column.text,
  updated_at: column.text,
  // (project_id, position) rather than project_id alone: the board's read is
  // `WHERE project_id = ? ORDER BY position`, and the narrower index left
  // SQLite doing a temp B-tree for the sort.
}, { indexes: { by_project_position: ['project_id', 'position'] } })

// by_project_position serves the board's main read; by_list serves the tail
// lookup that createCard and moveCard do before writing.
const board_cards = new Table({
  project_id: column.text,
  list_id: column.text,
  position: column.text,
  title: column.text,
  description: column.text,
  due_date: column.text,
  completed_at: column.text,
  assignee_user_id: column.text,
  assignee_contractor_id: column.text,
  linked_ticket_id: column.text,
  created_by: column.text,
  created_at: column.text,
  updated_at: column.text,
}, { indexes: {
  by_project_position: ['project_id', 'position'],
  by_list: ['list_id', 'position'],
} })

const board_contractor_links = new Table({
  project_id: column.text,
  contractor_id: column.text,
  token: column.text,
  expires_at: column.text,
  revoked_at: column.text,
  last_used_at: column.text,
  created_by: column.text,
  created_at: column.text,
  updated_at: column.text,
}, { indexes: { by_project: ['project_id'] } })

export const AppSchema = new Schema({
  users, projects, project_members, contractors, project_contractors,
  defect_tickets, analyses, analysis_metadata,
  defect_reports, trades,
  inspections, inspection_defects, defect_ticket_submissions,
  defect_types, defect_solutions,
  user_detection_preferences, email_templates,
  project_rooms, project_floor_plans,
  subscriptions, usage_records,
  system_settings, reminder_settings, reminder_log,
  board_lists, board_cards, board_contractor_links,
  analysis_local_thumbs, crud_dead_letters,
})
export type Database = (typeof AppSchema)['types']

/**
 * PG JSONB columns that PowerSync stores as TEXT in SQLite.
 *
 * The upload connector (`connector.ts`) JSON.parse()s these before sending
 * to PostgREST — without that decode, the stringified TEXT is written to
 * JSONB as a JSON string value, and every server reader that calls
 * `.map`/`.filter` on it crashes.
 *
 * Every JSONB column on a synced table MUST appear here. The coverage test
 * `__tests__/integration/powersync-jsonb-coverage.test.ts` fails CI if a
 * new JSONB column is added without registration — see that test for the
 * 2026-04-21 `inspections.participants` incident that prompted this guard.
 */
export const JSONB_COLUMNS: Record<string, readonly string[]> = {
  analysis_metadata: [
    'enabled_defect_types', 'enabled_trades', 'filter_by_trades',
    'raw_gemini_response', 'user_enabled_defects',
  ],
  defect_tickets: ['send_history'],
  defect_ticket_submissions: ['photos'],
  defect_types: ['agent_session_data'],
  inspections: ['participants'],
  project_rooms: ['bounding_box'],
  system_settings: [
    'enabled_trades', 'custom_trades', 'enabled_defects',
    'custom_defects', 'feature_flags',
  ],
  user_detection_preferences: [
    'enabled_trades', 'custom_trades', 'enabled_defects', 'custom_defects',
  ],
}
